import { ServerConfig } from "@/config/config";
import { Job } from "@/models/job";
import { Axios } from "@/utils/axios";

export interface ISaleSyncStatus {
	ID: number;
	WinfaktSaleID: number;
	ExactInvoiceID: string;
	Status: string;
	LastError: string;
	Attempts: number;
	LastSyncedAt: string;
}

export interface IPurchaseSyncStatus {
	ID: number;
	WinfaktPurchaseID: number;
	BookYear: number;
	ExactEntryID: string;
	Status: string;
	LastError: string;
	Attempts: number;
	LastSyncedAt: string;
}

export interface IExactPurchaseRef {
	ID:number;
	BookYear:number;
}

export interface IExportToExactOptions {
	bookYear?:number;
	beginDate?:Date;
	endDate?:Date;
	journalIds?:number[];
	saleIds?:number[];
	purchases?:IExactPurchaseRef[];
	includeAlreadySynced?:boolean;
}

export interface IExactExportError {
	FriendlyID:string;
	Error:string;
}

export interface IExactExportResult {
	Created:number;
	Skipped:number;
	Failed:number;
	Errors:IExactExportError[];
	Canceled:boolean;
}

export interface IExactRunSummary {
	Created:number;
	Skipped:number;
	Failed:number;
	Errors:IExactExportError[];
}

export interface IExactDossierRunResult {
	Sales:IExactRunSummary;
	Purchases:IExactRunSummary;
}

export interface IExactUnsyncedDocument {
	ID:number;
	BookYear:number;
	ComputedFriendlyID:string;
	InvoiceDate:string;
	ContactName:string;
	Total:number;
	IsCreditNote:boolean;
	SyncStatus:string;
	LastError:string;
	Attempts:number;
}

export interface IExactUnsyncedOverview {
	Sales:IExactUnsyncedDocument[];
	Purchases:IExactUnsyncedDocument[];
	SalesTruncated:boolean;
	PurchasesTruncated:boolean;
}

export interface IExactMarkExternalOptions {
	saleIds?:number[];
	purchases?:IExactPurchaseRef[];
	upTo?:string;
	includeSales?:boolean;
	includePurchases?:boolean;
}

export interface IExactMarkExternalResult {
	Sales:number;
	Purchases:number;
}

export class ExactSyncServiceClass {
	private readonly baseUrl:string = `${ServerConfig.host}/exact`;

	async pushSale(saleId:number):Promise<{InvoiceID:string, AttachmentError?:string}>{
		let result = await Axios.post(`${this.baseUrl}/push-sale/${saleId}`);
		return result.data;
	}

	async getSaleSync(saleId:number):Promise<ISaleSyncStatus | null>{
		let result = await Axios.get(`${this.baseUrl}/sale-sync/${saleId}`);
		return result.data || null;
	}

	async exportPeriod(options:IExportToExactOptions = {}):Promise<Job>{
		let body = {
			bookYear: options.bookYear || 0,
			beginDate: options.beginDate,
			endDate: options.endDate,
			journalIds: options.journalIds || [],
			saleIds: options.saleIds || [],
			includeAlreadySynced: options.includeAlreadySynced || false
		};
		let result = await Axios.post(`${this.baseUrl}/export-period`, body);
		return new Job(result.data);
	}

	async pushPurchase(purchaseId:number, bookYear:number):Promise<{EntryID:string, AttachmentError?:string}>{
		let result = await Axios.post(`${this.baseUrl}/push-purchase/${purchaseId}?bookYear=${bookYear}`);
		return result.data;
	}

	async getPurchaseSync(purchaseId:number, bookYear:number):Promise<IPurchaseSyncStatus | null>{
		let result = await Axios.get(`${this.baseUrl}/purchase-sync/${purchaseId}?bookYear=${bookYear}`);
		return result.data || null;
	}

	async exportPurchasePeriod(options:IExportToExactOptions = {}):Promise<Job>{
		let body = {
			bookYear: options.bookYear || 0,
			beginDate: options.beginDate,
			endDate: options.endDate,
			journalIds: options.journalIds || [],
			purchases: options.purchases || [],
			includeAlreadySynced: options.includeAlreadySynced || false
		};
		let result = await Axios.post(`${this.baseUrl}/export-purchases`, body);
		return new Job(result.data);
	}

	async getUnsynced(from?:string):Promise<IExactUnsyncedOverview>{
		let url = `${this.baseUrl}/unsynced`;
		if (from) url += `?from=${from}`;
		let result = await Axios.get(url);
		return result.data;
	}

	async markExternal(options:IExactMarkExternalOptions):Promise<IExactMarkExternalResult>{
		let body = {
			saleIds: options.saleIds || [],
			purchases: options.purchases || [],
			upTo: options.upTo || "",
			includeSales: options.includeSales || false,
			includePurchases: options.includePurchases || false
		};
		let result = await Axios.post(`${this.baseUrl}/mark-external`, body);
		return result.data;
	}

	async runAutoSyncNow(date?:string):Promise<IExactDossierRunResult>{
		let url = `${this.baseUrl}/run-auto-sync`;
		if (date) url += `?date=${date}`;
		let result = await Axios.post(url);
		return result.data;
	}
}

export const ExactSyncService = new ExactSyncServiceClass();
